import React from "react";
import { tokens } from "../../public/tokens.js";

/**
 * Returns the style object for a result badge
 * based on the assessment outcome.
 */
export const getResultStyle = (result) => {
    const baseStyle = {
        display: "flex",
        height: "22px",
        padding: "0 6px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "2px",
        borderRadius: "10px",
        fontSize: tokens.typography.fontSize["md-12px"],
        fontWeight: tokens.typography.fontWeights.semibold,
        borderWidth: "1px",
        borderStyle: "solid",
    };

    switch (result) {
        case "Pass":
            return {
                ...baseStyle,
                borderColor: tokens.colors.states.success["500"],
                background: tokens.colors.states.success["500"],
                color: tokens.colors.neutral.white,
            };

        case "Fail":
            return {
                ...baseStyle,
                borderColor: tokens.colors.states.alert["500"],
                background: tokens.colors.states.alert["500"],
                color: tokens.colors.neutral.white,
            };

        case "Pending":
            return {
                ...baseStyle,
                borderColor: "rgba(173, 181, 189, 0.2)",
                background: "rgba(173, 181, 189, 0.2)",
                color: tokens.colors.neutral.grey["800"],
            };

        case "Exempted":
            return {
                ...baseStyle,
                borderColor: tokens.colors.brand["veld-sprout"]["700"],
                background: tokens.colors.brand["veld-sprout"]["700"],
                color: tokens.colors.neutral.white,
            };

        case "Withheld":
            return {
                ...baseStyle,
                borderColor: tokens.colors.brand["burnt-orange"]["600"],
                background: tokens.colors.brand["burnt-orange"]["600"],
                color: tokens.colors.neutral.white,
                textTransform: "capitalize",
            };

        case "Condoned":
            return {
                ...baseStyle,
                borderColor: tokens.colors.brand["veld-sprout"]["700"],
                background: tokens.colors.brand["veld-sprout"]["700"],
                color: tokens.colors.neutral.white,
            };

        case "Not Submitted":
            return {
                ...baseStyle,
                borderColor: tokens.colors.states.alert["500"],
                background: tokens.colors.states.alert["500"],
                color: tokens.colors.neutral.white,
            };


        default:
            return baseStyle;
    }
};

/**
 * Returns the text style for a mark
 * based on the overall result.
 */
export const getMarkColor = (result) => {

    const baseStyle = {
        fontSize: tokens.typography.fontSize["base-16px"],
        fontWeight: tokens.typography.fontWeights.bold,
    };

    switch (result) {
        case "Pass":
            return {
                ...baseStyle,
                color: tokens.colors.brand["fresh-green"]["900"],
            };
        case "Fail":
            return {
                ...baseStyle,
                color: tokens.colors.brand.red["700"],
            };
        case "Withheld":
            return {
                ...baseStyle,
                color: tokens.colors.brand["burnt-orange"]["600"],
                textTransform: "uppercase",
            };
        case "Condoned":
            return {
                ...baseStyle,
                color: tokens.colors.brand["veld-sprout"]["700"],
            };
        case "Not Submitted":
            return {
                ...baseStyle,
                color: tokens.colors.brand.red["700"],
            };
        default:
            return {
                ...baseStyle,
            }
    }
};

/**
 * Formats a result value into a
 * user-friendly display string.
 */
export const formatResult = (value) => {
    if (!value) return "-";

    switch (value.toLowerCase()) {
        case "pass":
            return "Pass";
        case "fail":
            return "Fail";
        case "pending":
            return "Pending";
        case "exempted":
            return "Exempted";
        case "withheld":
            return "Withheld";
        case "condoned":
            return "Condoned";
        case "not submitted":
            return "Not Submitted";   
        default:
            return "-";
    }
};

/**
 * Returns the inline CSS for a result badge.
 */
const pdfGetResultStyle = (result) => {
    const baseStyle = `
        display:flex;
        height:22px;
        padding:0 6px;
        flex-direction:column;
        justify-content:center;
        align-items:center;
        gap:2px;
        border-radius:${tokens.globals["border-radius"]};
        font-size:${tokens.typography.fontSize["md-12px"]};
        font-weight:${tokens.typography.fontWeights.semibold};
        border-width:1px;
        border-style:solid;
    `;

    switch (result) {
        case "Pass":
            return `
                ${baseStyle}
                border-color:${tokens.colors.states.success["500"]};
                background:${tokens.colors.states.success["500"]};
                color:${tokens.colors.neutral.white};
            `;

        case "Fail":
            return `
                ${baseStyle}
                border-color:${tokens.colors.states.alert["500"]};
                background:${tokens.colors.states.alert["500"]};
                color:${tokens.colors.neutral.white};
            `;

        case "Pending":
            return `
                ${baseStyle}
                border-color:rgba(173,181,189,.2);
                background:rgba(173,181,189,.2);
                color:${tokens.colors.neutral.grey["800"]};
            `;

        case "Exempted":
            return `
                ${baseStyle}
                border-color:${tokens.colors.brand["veld-sprout"]["700"]};
                background:${tokens.colors.brand["veld-sprout"]["700"]};
                color:${tokens.colors.neutral.white};
            `;

        case "Withheld":
            return `
                ${baseStyle}
                border-color:${tokens.colors.brand["burnt-orange"]["600"]};
                background:${tokens.colors.brand["burnt-orange"]["600"]};
                color:${tokens.colors.neutral.white};
                text-transform:capitalize;
            `;

        case "Condoned":
            return `
                ${baseStyle}
                border-color:${tokens.colors.brand["veld-sprout"]["700"]};
                background:${tokens.colors.brand["veld-sprout"]["700"]};
                color:${tokens.colors.neutral.white};
            `;

        case "Not Submitted":
            return `
                ${baseStyle}
                border-color:${tokens.colors.states.alert["500"]};
                background:${tokens.colors.states.alert["500"]};
                color:${tokens.colors.neutral.white};
            `;

        default:
            return baseStyle;
    }
};
/**
* Returns the inline CSS for the
* overall course mark.
*/
const pdfGetMarkStyle = (result) => {
    const baseStyle = `
        font-size:${tokens.typography.fontSize["base-16px"]};
        font-weight:${tokens.typography.fontWeights.bold};
    `;

    switch (result) {
        case "Pass":
            return `
                ${baseStyle}
                color:${tokens.colors.states.success["900"]};
            `;

        case "Fail":
            return `
                ${baseStyle}
                color:${tokens.colors.states.alert["700"]};
            `;

        case "Withheld":
            return `
                ${baseStyle}
                color:${tokens.colors.brand["burnt-orange"]["600"]};
                text-transform:uppercase;
            `;

        case "Condoned":
            return `
                ${baseStyle}
                color:${tokens.colors.brand["veld-sprout"]["700"]};
            `;

        default:
            return baseStyle;
    }
};

/** Displays the appropriate coloured status with a circular
 * indicator for "Incomplete", "Competent", or
 * "Not Yet Competent".
 * */
const pdfRobotResult = (gradeText) => {
    let text;
    let color;

    switch (gradeText) {
        case "Incomplete":
            text = "Incomplete";
            color = "red";
            break;
        case "Competent":
            text = "Competent";
            color = "#58ba62";
            break;
        default:
            text = "Not Yet Competent";
            color = "#ffa62c";
            break;
    }

    return `
        <div style="display:flex; align-items:center; justify-content:center; gap:4px; color:${color};">
            <span style="font-size:6px; margin-right:1px;">●</span>
            <span style="
                font-size:12px;
                white-space:nowrap;
                overflow:hidden;
                text-overflow:ellipsis;
            ">
                ${text}
            </span>
        </div>
    `;
};

const today = new Date();
const todayLong = today.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", });

export const buildGradebookHtml = ({
    rows,
    summary,
    intakeLockStatus,
    studentName,
    studentNum,
    idPassport,
    subjectName,
    subjectIntake,
    subjectDuration,
}) => {

    const buildOverallResultsSection = () => {
        if (intakeLockStatus === 1) {
            return `

<div class="overallResultsBox">

    <div class="infoColumn">

        <div class="courseResult">
            Course Results
        </div>

        <div>
            Overall course performance summary
        </div>

    </div>

    <div class="markResultColumn">

        <div>
            Grade
        </div>

        <div style="${pdfGetMarkStyle(
                formatResult(summary.overallResult)
            )}">
            ${summary.overallGrade ?? "-"}
        </div>

    </div>

    <div class="markResultColumn">

        <div>
            Outcome
        </div>

        <div style="${pdfGetResultStyle(
                formatResult(summary.overallResult) ?? "Pending"
            )}">
            ${formatResult(summary.overallResult) ?? "Pending"}
        </div>

    </div>

</div>

`;
        }

        return "";
    };


    const buildAssessmentRows = (rows) => {
        if (!rows.length) {
            return "";
        }

        return rows
            .map((item, index) => `
<div
    class="tableContent ${(item.result === "Fail" || item.result === "Not Submitted") ? "failRow" : ""}"
    style="${index === rows.length - 1 ? "border-bottom:none;" : ""}"
>

    <div class="assessmentCell">
        <span class="assessmentName">${item.name}</span>

        <span>
            <span class="assessmentType">${item.type}</span>
            <span class="metaData">${item.code}</span>
        </span>
    </div>

    <div class="markResultCell">
        <span class="${item.isWithheld ? "withheldText" : "assessmentMark"}">
            ${item.gradingType === "1"
                    ? pdfRobotResult(item.mark)
                    : item.mark}
        </span>
    </div>

    <div class="markResultCell" style="border-right:none;">
        <span style="${pdfGetResultStyle(item.result ?? "Pending")}">
            ${item.result ?? "Pending"}
        </span>
    </div>

</div>
`)
            .join("");
    };

    const getSoRPdfStyles = () => `
    body{
    display: flex;
    width: 865px;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    font-family: ${tokens.globals["font-family"]};
}

.gradeBook{
    display:flex;
    flex-direction:column;
    align-items:flex-start;
    gap:24px;
    width:100%;
    align-self:stretch;
}

.overallResultsBox{
    display:flex;
    width:100%;
    background:${tokens.colors.surfaces["container-background"]};
}

.infoColumn{
    display:flex;
    flex-direction:column;
    justify-content:center;
    align-items:flex-start;
    flex:1 0 0;
    gap:10px;
    padding:12px 0 12px 24px;
}

.courseResult{
    font-size:${tokens.typography.fontSize["base-16px"]};
    font-weight:${tokens.typography.fontWeights.bold};
    line-height:16.8px;
}

.markResultColumn{
    display:flex;
    width:20%;
    padding:12px;
    flex-direction:column;
    justify-content:center;
    align-items:center;
    gap:10px;
}

.tableResultBox{
    display:flex;
    align-items:center;
    align-self:stretch;
    flex-direction:column;
    width:100%;
    border:1px solid ${tokens.colors.surfaces["border-divider"]};
    border-radius:${tokens.globals["border-radius"]};
    overflow:hidden;
    background:${tokens.colors.surfaces["container-background"]};
}

.tableHeader{
    display:flex;
    width:100%;
    height:56px;
    background:${tokens.colors.surfaces["container-background"]};
    border-bottom:1px solid ${tokens.colors.surfaces["border-divider"]};
    font-size: ${tokens.typography.fontSize["lg-14px"]};
    font-weight:${tokens.typography.fontWeights.semibold};
}

.tableContent{
    display:flex;
    width:100%;
    min-height:56px;
    align-items: stretch;
    background:${tokens.colors.surfaces["container-background"]};
    border-bottom:1px solid ${tokens.colors.surfaces["border-divider"]};
    break-inside: avoid;
}

.failRow{
    background:rgba(196,62,81,.05);
}

.assessmentCell{
    display:flex;
    flex-direction:column;
    justify-content:center;
    align-items:flex-start;
    flex:1 0 0;
    min-width:0;
    padding:12px 0 12px 24px;
    border-right:1px solid ${tokens.colors.surfaces["border-divider"]};
}

.markResultCell{
    display:flex;
    width:20%;
    padding:0 12px;
    justify-content:center;
    align-items:center;
    flex-direction:column;
    gap:12px;
    border-right:1px solid ${tokens.colors.surfaces["border-divider"]};
}

.assessmentName{
    width:100%;
    color:${tokens.colors.brand["aie-blue"]["700"]};
    font-size: ${tokens.typography.fontSize["lg-14px"]};
    font-weight:${tokens.typography.fontWeights.semibold};
    line-height:110%;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
}

.assessmentMark{
    display:flex;
    width:59px;
    height:30px;
    justify-content:center;
    align-items:center;
    font-size: ${tokens.typography.fontSize["lg-14px"]};
    color:${tokens.colors.neutral.grey["800"]};
}

.assessmentType {
    font-size: ${tokens.typography.fontSize["md-12px"]};
    font-weight: ${tokens.typography.fontWeights.regular};
    color:${tokens.colors.text.secondary};
}

.metaData{
    font-size: ${tokens.typography.fontSize["md-12px"]};
    color:${tokens.colors.text.secondary};;
}

.withheldText{
    color:${tokens.colors.brand["burnt-orange.600"]};
    font-weight: ${tokens.typography.fontWeights.semibold};
    text-transform:uppercase;
}

.headerBox{
    display: flex;
    padding-top: 24px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    align-self: stretch;
}

.titleHeading{
    height: 49px;
    align-self: stretch;
    color:${tokens.colors.brand["aie-blue"]["700"]};
    font-size: 30px;
    font-weight: ${tokens.typography.fontWeights.semibold};
}

.courseHeading{
    height: 49px;
    align-self: stretch;
    color:${tokens.colors.brand["aie-blue"]["700"]};
    font-size:${tokens.typography.fontSize["2xl-20px"]};
    font-weight:${tokens.typography.fontWeights.regular};
}

.infoBox{
    display: flex;
    padding: 24px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius:${tokens.globals["border-radius"]};
    background:${tokens.colors.surfaces["page-background"]};
}

.infoContent{
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
}

.infoCol{
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
}

.titleText{
    color:${tokens.colors.brand["aie-blue"]["700"]};
    font-size: ${tokens.typography.fontSize["lg-14px"]};
    font-weight:${tokens.typography.fontWeights.bold};
    line-height: 14px;
}

.infoText{
    color: #000;
    font-size: ${tokens.typography.fontSize["lg-14px"]};
    font-weight:${tokens.typography.fontWeights.regular};
    line-height: 14px;
}

.bottomSection{
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 30px;
    align-self: stretch;
}

.noteText{
    color:${tokens.colors.brand["aie-blue"]["700"]};
    font-size: ${tokens.typography.fontSize["lg-14px"]};
    font-weight:${tokens.typography.fontWeights.bold};
    line-height: 150%;
}

.text{
    color:${tokens.colors.brand["aie-blue"]["700"]};
    font-size:${tokens.typography.fontSize["md-12px"]};
    font-weight:${tokens.typography.fontWeights.regular};
    line-height: 150%;
}

.atRisk{
    font-weight:${tokens.typography.fontWeights.semibold};
}

.gradingTable{
    width:340px;
    border-collapse:collapse;
    border:1px solid ${tokens.colors.neutral.black};
    font-size:${tokens.typography.fontSize["sm-10px"]};
}

.gradingTable td{
    border:1px solid ${tokens.colors.neutral.black};;
    padding:8px 10px;
    vertical-align:top;
}

.firstColumn{
    width:50%;
    font-weight:${tokens.typography.fontWeights.semibold};
}

.secondColumn{
    width:50%;
    font-weight:${tokens.typography.fontWeights.regular};
}

.signatureImage{
    height: 100px;
    width: auto;
    max-width: 100%;
}   
    `;

    return`

<!DOCTYPE html>

<html>

<head>

<meta charset="UTF-8"/>

<style>

${getSoRPdfStyles()}

</style>

</head>

<body>

<div class="headerBox">
    <div class="titleHeading">Provisional Statement of Results</div>
    <div class="courseHeading">${subjectName}</div>
    <div class="infoBox">
        <div class="infoContent">
            <div class="infoCol">
                <div class="titleText">
                    Student Name:
                    <span class= "infoText">${studentName}</span>
                </div>
                <div class="titleText">
                    Student Number:
                    <span class= "infoText">${studentNum}</span>
                </div>
                <div class="titleText">
                    ID/Passport Number:
                    <span class= "infoText">${idPassport}</span>
                </div>
            </div>
            <div class="infoCol">
                <div class="titleText">
                    Course Duration:
                    <span class= "infoText">${subjectDuration}</span>
                </div>
                <div class="titleText">
                    Intake:
                    <span class= "infoText">${subjectIntake}</span>
                </div>
                <div class="titleText">
                    Date Issued:
                    <span class= "infoText">${todayLong}</span>
                </div>
            </div>
        </div>
    </div>
</div>

<div class="gradeBook">

${buildOverallResultsSection()}

<div class="tableResultBox">

    <div class="tableHeader">

        <div class="assessmentCell">

            <span>
                Assessments
            </span>

        </div>

        <div class="markResultCell">

            <span>
                Grade
            </span>

        </div>

        <div
            class="markResultCell"
            style="border-right:none;"
        >

            <span>
                Result
            </span>

        </div>

    </div>
        ${buildAssessmentRows(rows)}
    </div>

    
</body>

</html>
`;


};
